import { useState } from 'react';
import {
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  Plus,
  Type,
  UserRound,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui-kit/collapsible';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { ContactPicker } from '@/modules/org/contacts/components/ContactPicker';
import { useCompanyLookup } from '@/modules/org/companies/hooks/use-companies';
import {
  companySubKindsTaken,
  subKindsTaken,
  toIdentifier,
  uniqueEntityKey,
} from './body-model';
import { useDocxSelection } from './context';
import {
  COMPANY_SUB_ICON,
  COMPANY_SUB_KINDS,
  COMPANY_SUB_LABELS,
  CURRENCY_OPTIONS,
  SUB_ICON,
  SUB_KINDS,
  SUB_LABELS,
  TYPE_OPTIONS,
  type CompanySubKind,
  type DraftSelection,
  type ScalarType,
  type SubKind,
} from './types';

export function VariableEditor() {
  const {
    draft,
    draftError,
    variables,
    persons,
    companies,
    updateDraft,
    commitDraft,
    cancelDraft,
  } = useDocxSelection();
  const [optionInput, setOptionInput] = useState('');
  const [signerOpen, setSignerOpen] = useState(false);
  // Must stay above the early return below — and only the default-signer section reads it,
  // so it's gated on that section being expanded rather than fetching on every render.
  const { byId: companyById } = useCompanyLookup(signerOpen);

  if (!draft) return null;
  const editing = draft.editingIndex !== null;

  const addChoice = () => {
    const val = optionInput.trim();
    if (!val) return;
    if (draft.options.includes(val)) {
      setOptionInput('');
      return;
    }
    updateDraft({ options: [...draft.options, val] });
    setOptionInput('');
  };

  const set = (patch: Partial<DraftSelection>) => updateDraft(patch);

  const editingName = editing ? variables[draft.editingIndex as number]?.name : undefined;
  const taken = subKindsTaken(variables, draft.personKey, editingName);
  const companyTaken = companySubKindsTaken(variables, draft.companyKey, editingName);
  const personOptions = Object.values(persons);
  // Company entities already declared in this document — attach targets for a new sub-field.
  const companyEntityOptions = Object.values(companies);
  // Person and company keys share one namespace, so new-key allocation must see both.
  const entityKeys = [...Object.keys(persons), ...Object.keys(companies)];
  // Candidate sources for "Mirror value from": plain fields that aren't copies themselves,
  // excluding the field being edited (no self / chained links).
  const copySourceOptions = variables.filter((v) => !v.valueCopyFrom && v.name !== editingName);
  const copySourceLabel = draft.valueCopyFrom
    ? (variables.find((v) => v.name === draft.valueCopyFrom)?.label ?? draft.valueCopyFrom)
    : '';
  // Other persons that already have a company sub-field — candidates to share a company with.
  const companyOptions = Object.values(persons)
    .filter(
      (p) =>
        p.key !== draft.personKey &&
        variables.some((c) => c.personKey === p.key && c.subKind === 'company')
    )
    .map((p) => ({ ref: p.companyRef ?? p.key, label: p.companyLabel || p.label }));
  // Next free signing slot: count existing signatories (excluding this person) + 1, so a
  // new signer defaults to 2, 3, … instead of everyone landing on 1.
  const nextSigningOrder =
    Object.values(persons).filter((p) => p.isSignatory && p.key !== draft.personKey).length + 1;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        <button
          onClick={cancelDraft}
          className="p-1 rounded-md hover:bg-muted text-globe-grey transition-colors"
          aria-label="Back to fields"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-globe-grey">{editing ? 'Editing field' : 'Marking'}</p>
          <p className="text-sm font-medium text-[hsl(var(--fg))] truncate">
            &ldquo;{draft.selectedText}&rdquo;
          </p>
        </div>
        <button
          onClick={cancelDraft}
          className="hidden md:block p-1 rounded-md hover:bg-muted text-globe-grey transition-colors"
          aria-label="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Field kind */}
        <div className="space-y-1.5">
          <Label className="text-xs">Mark this as</Label>
          <div className="grid grid-cols-3 gap-1 rounded-lg border border-border p-0.5">
            {[
              { k: 'field' as const, label: 'Fill-in', icon: Type },
              { k: 'person' as const, label: 'Person', icon: UserRound },
              { k: 'company' as const, label: 'Company', icon: Building2 },
            ].map(({ k, label, icon: Icon }) => {
              const active = draft.kind === k;
              return (
                <button
                  key={k}
                  type="button"
                  disabled={editing && draft.kind !== k}
                  onClick={() => set({ kind: k })}
                  aria-pressed={active}
                  className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 ${
                    active ? 'bg-selise-blue text-white' : 'text-globe-grey hover:bg-muted'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {draft.kind === 'field' ? (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="draft-label" className="text-xs">
                Field label
              </Label>
              <Input
                id="draft-label"
                value={draft.label}
                onChange={(e) => set({ label: e.target.value })}
                className="h-9"
              />
            </div>

            {copySourceOptions.length > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor="draft-copyfrom" className="text-xs">
                  Mirror value from
                </Label>
                <select
                  id="draft-copyfrom"
                  value={draft.valueCopyFrom}
                  onChange={(e) => set({ valueCopyFrom: e.target.value })}
                  className="flex h-9 w-full rounded-lg border border-input bg-transparent px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue"
                >
                  <option value="">— None (its own value)</option>
                  {copySourceOptions.map((s) => (
                    <option key={s.name} value={s.name}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-globe-grey">
                  Mirror another field&apos;s value here — the filler enters it once and it appears
                  in both spots.
                </p>
              </div>
            )}

            {draft.valueCopyFrom ? (
              <p className="text-xs text-globe-grey bg-muted/40 rounded-md p-2.5">
                Mirrors <span className="font-medium text-[hsl(var(--fg))]">{copySourceLabel}</span>{' '}
                — filled once, appears here too. Its type, required setting and hints come from that
                field.
              </p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="draft-type" className="text-xs">
                    How will colleagues fill this in?
                  </Label>
                  <select
                    id="draft-type"
                    value={draft.type}
                    onChange={(e) => set({ type: e.target.value as ScalarType })}
                    className="flex h-9 w-full rounded-lg border border-input bg-transparent px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue"
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {draft.type === 'currency' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="draft-currency" className="text-xs">
                      Currency
                    </Label>
                    <select
                      id="draft-currency"
                      value={draft.currencyCode}
                      onChange={(e) => set({ currencyCode: e.target.value })}
                      className="flex h-9 w-full rounded-lg border border-input bg-transparent px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue"
                    >
                      {CURRENCY_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-globe-grey">
                      Shown next to the amount (e.g. {draft.currencyCode} 165,000).
                    </p>
                  </div>
                )}
                {draft.type === 'select' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="draft-options" className="text-xs">
                      Choices
                    </Label>
                    {draft.options.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {draft.options.map((opt) => (
                          <Badge key={opt} variant="secondary" className="gap-1 pr-1">
                            {opt}
                            <button
                              type="button"
                              onClick={() =>
                                set({ options: draft.options.filter((o) => o !== opt) })
                              }
                              className="ml-0.5 rounded-full hover:text-crimson"
                              aria-label={`Remove ${opt}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        id="draft-options"
                        value={optionInput}
                        onChange={(e) => setOptionInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addChoice();
                          }
                        }}
                        placeholder="Type a choice, press Enter"
                        className="h-9"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0"
                        onClick={addChoice}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.required}
                    onChange={(e) => set({ required: e.target.checked })}
                    className="size-4 rounded border-border accent-selise-blue"
                  />
                  Required
                </label>
                <details className="rounded-md">
                  <summary className="cursor-pointer list-none text-xs text-globe-grey hover:text-[hsl(var(--fg))] select-none">
                    Advanced
                  </summary>
                  <div className="space-y-4 pt-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="draft-placeholder" className="text-xs">
                        Placeholder
                      </Label>
                      <Input
                        id="draft-placeholder"
                        value={draft.placeholder}
                        onChange={(e) => set({ placeholder: e.target.value })}
                        className="h-9"
                        placeholder="e.g. Jane Doe"
                      />
                      <p className="text-[11px] text-globe-grey">
                        Faint hint shown inside the empty field when colleagues fill it in.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="draft-helptext" className="text-xs">
                        Help text
                      </Label>
                      <Input
                        id="draft-helptext"
                        value={draft.helpText}
                        onChange={(e) => set({ helpText: e.target.value })}
                        className="h-9"
                        placeholder="Short guidance shown under the field"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="draft-name" className="text-xs">
                        Internal reference
                      </Label>
                      <Input
                        id="draft-name"
                        value={draft.name}
                        disabled
                        className="h-9 font-mono text-xs"
                      />
                      <p className="text-[11px] text-globe-grey">
                        Used in the generated file. Generated automatically — can&apos;t be changed.
                      </p>
                    </div>
                  </div>
                </details>
              </>
            )}
          </>
        ) : draft.kind === 'person' ? (
          <>
            {/* Belongs to */}
            {!editing && (
              <div className="space-y-1.5">
                <Label className="text-xs">Belongs to</Label>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    aria-pressed={draft.newPerson}
                    onClick={() =>
                      set({
                        newPerson: true,
                        personKey: uniqueEntityKey(
                          variables,
                          entityKeys,
                          toIdentifier(draft.selectedText)
                        ),
                        personLabel: draft.selectedText,
                        subKind: 'name',
                        required: true,
                      })
                    }
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      draft.newPerson
                        ? 'border-selise-blue bg-selise-blue text-white'
                        : 'border-border text-[hsl(var(--fg))] hover:bg-muted'
                    }`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New person
                  </button>
                  {personOptions.map((p) => {
                    const active = !draft.newPerson && draft.personKey === p.key;
                    const full = SUB_KINDS.every((s) => subKindsTaken(variables, p.key).has(s));
                    return (
                      <button
                        key={p.key}
                        type="button"
                        disabled={full && !active}
                        aria-pressed={active}
                        title={full ? "All of this person's fields are already marked" : undefined}
                        onClick={() => {
                          const t = subKindsTaken(variables, p.key);
                          const free = (SUB_KINDS.find((s) => !t.has(s)) ?? 'role') as SubKind;
                          set({
                            newPerson: false,
                            personKey: p.key,
                            personLabel: p.label,
                            subKind: free,
                            required: free === 'name',
                          });
                        }}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          active
                            ? 'border-selise-blue bg-selise-blue text-white'
                            : 'border-border text-[hsl(var(--fg))] hover:bg-muted'
                        }`}
                      >
                        <UserRound className="h-3.5 w-3.5" />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Which sub-field */}
            {editing ? (
              <p className="text-xs text-globe-grey">
                This is{' '}
                <span className="font-medium text-[hsl(var(--fg))]">
                  {draft.personLabel || draft.personKey}
                </span>
                ’s <span className="font-medium">{SUB_LABELS[draft.subKind]}</span>.
              </p>
            ) : draft.newPerson ? (
              <p className="text-xs text-globe-grey">
                Marking the person’s <span className="font-medium">Name</span>. Mark their other
                details (role, company, email, phone, address) afterwards and attach each to this
                person.
              </p>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">This text is the…</Label>
                <div className="grid grid-cols-3 gap-1">
                  {SUB_KINDS.map((s) => {
                    const Icon = SUB_ICON[s];
                    const disabled = taken.has(s);
                    const active = draft.subKind === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        disabled={disabled}
                        onClick={() => set({ subKind: s, required: s === 'name' })}
                        aria-pressed={active}
                        className={`flex items-center justify-center gap-1 rounded-md border px-1.5 py-1.5 text-xs transition-colors disabled:opacity-30 ${
                          active
                            ? 'border-selise-blue bg-selise-blue/10 text-selise-blue font-medium'
                            : 'border-border text-globe-grey hover:bg-muted'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {SUB_LABELS[s]}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Person label — owned by the Name sub-field */}
            {(draft.newPerson || draft.subKind === 'name') && (
              <div className="space-y-1.5">
                <Label htmlFor="draft-person-label" className="text-xs">
                  Person label
                </Label>
                <Input
                  id="draft-person-label"
                  value={draft.personLabel}
                  onChange={(e) => set({ personLabel: e.target.value })}
                  className="h-9"
                  placeholder="e.g. Employer"
                />
                <p className="text-[11px] text-globe-grey">
                  A role-style name colleagues recognise — e.g. “Employer”, “Tenant”. The actual
                  name is filled in when the document is used.
                </p>
              </div>
            )}

            {/* Company reuse — owned by the Company sub-field. */}
            {draft.subKind === 'company' && (
              <div className="space-y-1.5">
                <Label htmlFor="draft-company-link" className="text-xs">
                  Company
                </Label>
                {companyOptions.length > 0 && (
                  <select
                    id="draft-company-link"
                    value={draft.companyRef ?? '__own'}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__own') set({ companyRef: null });
                      else {
                        const opt = companyOptions.find((c) => c.ref === val);
                        set({ companyRef: val, companyLabel: opt?.label ?? draft.companyLabel });
                      }
                    }}
                    className="flex h-9 w-full rounded-lg border border-input bg-transparent px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue"
                  >
                    <option value="__own">Its own company</option>
                    {companyOptions.map((c) => (
                      <option key={c.ref} value={c.ref}>
                        Same company as {c.label}
                      </option>
                    ))}
                  </select>
                )}
                <Input
                  value={draft.companyLabel ?? ''}
                  onChange={(e) => set({ companyLabel: e.target.value })}
                  className="h-9"
                  placeholder="Company label (e.g. Employer company)"
                />
                <p className="text-[11px] text-globe-grey">
                  Reuse a company so colleagues fill its name once and it appears in every linked
                  spot.
                </p>
              </div>
            )}

            {/* Signature — a location anchor, not a fill-in. */}
            {draft.subKind === 'signature' && (
              <p className="text-xs text-globe-grey bg-muted/40 rounded-md p-2.5">
                Marks where{' '}
                <span className="font-medium text-[hsl(var(--fg))]">
                  {draft.personLabel || draft.personKey}
                </span>{' '}
                signs — colleagues don&apos;t fill anything here, and the text stays as-is in the
                document. Make sure this person is set as a signatory on their{' '}
                <span className="font-medium">Name</span> field.
              </p>
            )}

            {/* Required is meaningless for a signature anchor (nothing is filled). */}
            {draft.subKind !== 'signature' && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.required}
                  onChange={(e) => set({ required: e.target.checked })}
                  className="size-4 rounded border-border accent-selise-blue"
                />
                Required
              </label>
            )}

            {/* Signatory — owned by the Name sub-field. */}
            {draft.subKind === 'name' && (
              <div className="space-y-2 rounded-md border border-border p-2.5">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.isSignatory}
                    onChange={(e) =>
                      set({
                        isSignatory: e.target.checked,
                        signingOrder: e.target.checked
                          ? (draft.signingOrder ?? nextSigningOrder)
                          : null,
                      })
                    }
                    className="size-4 rounded border-border accent-selise-blue"
                  />
                  This person signs the document
                </label>
                {draft.isSignatory && (
                  <div className="flex items-center gap-2 pl-6">
                    <Label htmlFor="draft-order" className="text-xs">
                      Signing order
                    </Label>
                    <Input
                      id="draft-order"
                      type="number"
                      min={1}
                      value={draft.signingOrder ?? nextSigningOrder}
                      onChange={(e) =>
                        set({ signingOrder: Math.max(1, Number(e.target.value) || 1) })
                      }
                      className="h-8 w-20"
                    />
                  </div>
                )}
                <p className="pl-6 text-[11px] text-globe-grey">
                  A signing email becomes required when the document is sent for signature. It isn’t
                  shown in the document.
                </p>
              </div>
            )}

            {/* Default signer — owned by the Name sub-field. Frozen snapshot that pre-fills.
                Collapsed by default; the trigger surfaces whether details are already set. */}
            {draft.subKind === 'name' &&
              (() => {
                const signer = draft.defaultSigner;
                const signerSummary =
                  signer.name || signer.email || signer.role || signer.company || '';

                return (
                  <Collapsible
                    open={signerOpen}
                    onOpenChange={setSignerOpen}
                    className="rounded-md border border-border"
                  >
                    <CollapsibleTrigger className="flex w-full items-center gap-2 p-2.5 text-left">
                      <div className="min-w-0 flex-1">
                        <span className="text-xs font-medium">Default signer (optional)</span>
                        {!signerOpen && signerSummary && (
                          <span className="ml-2 text-[11px] text-globe-grey">{signerSummary}</span>
                        )}
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-globe-grey transition-transform ${
                          signerOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </CollapsibleTrigger>

                    <CollapsibleContent className="space-y-2 px-2.5 pb-2.5">
                      <div className="flex justify-end">
                        <ContactPicker
                          label="Pick contact"
                          canShareWithOrg
                          align="end"
                          createDefaults={{
                            name: signer.name,
                            email: signer.email,
                            role: signer.role,
                            company: signer.company,
                          }}
                          onSelect={(c) =>
                            set({
                              defaultSigner: {
                                name: c.name ?? '',
                                email: c.email ?? '',
                                role: c.role ?? '',
                                // Snapshot the company's current name, not the contact's
                                // possibly-stale copy of it.
                                company:
                                  (c.companyId ? companyById.get(c.companyId)?.name : '') ||
                                  c.company ||
                                  '',
                              },
                            })
                          }
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {(
                          [
                            ['name', 'Name'],
                            ['email', 'Email'],
                            ['role', 'Role'],
                            ['company', 'Company'],
                          ] as const
                        ).map(([k, lbl]) => (
                          <div key={k} className="space-y-1">
                            <Label htmlFor={`signer-${k}`} className="text-[11px] text-globe-grey">
                              {lbl}
                            </Label>
                            <Input
                              id={`signer-${k}`}
                              type={k === 'email' ? 'email' : 'text'}
                              value={signer[k]}
                              onChange={(e) =>
                                set({ defaultSigner: { ...signer, [k]: e.target.value } })
                              }
                              className="h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-globe-grey">
                        Pre-fills this person when the template is used — anyone can change it.
                        Picking a contact just copies its details here.
                      </p>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })()}
          </>
        ) : (
          <>
            {/* Belongs to — declare a new company or attach to one already declared. */}
            {!editing && (
              <div className="space-y-1.5">
                <Label className="text-xs">Belongs to</Label>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    aria-pressed={draft.newCompany}
                    onClick={() =>
                      set({
                        newCompany: true,
                        companyKey: uniqueEntityKey(
                          variables,
                          entityKeys,
                          toIdentifier(draft.selectedText)
                        ),
                        companyEntityLabel: draft.selectedText,
                        companySubKind: 'name',
                        required: true,
                      })
                    }
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      draft.newCompany
                        ? 'border-selise-blue bg-selise-blue text-white'
                        : 'border-border text-[hsl(var(--fg))] hover:bg-muted'
                    }`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New company
                  </button>
                  {companyEntityOptions.map((c) => {
                    const active = !draft.newCompany && draft.companyKey === c.key;
                    const full = COMPANY_SUB_KINDS.every((s) =>
                      companySubKindsTaken(variables, c.key).has(s)
                    );
                    return (
                      <button
                        key={c.key}
                        type="button"
                        disabled={full && !active}
                        aria-pressed={active}
                        title={full ? "All of this company's fields are already marked" : undefined}
                        onClick={() => {
                          const t = companySubKindsTaken(variables, c.key);
                          const free = (COMPANY_SUB_KINDS.find((s) => !t.has(s)) ??
                            'address') as CompanySubKind;
                          set({
                            newCompany: false,
                            companyKey: c.key,
                            companyEntityLabel: c.label,
                            companySubKind: free,
                            required: free === 'name',
                          });
                        }}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          active
                            ? 'border-selise-blue bg-selise-blue text-white'
                            : 'border-border text-[hsl(var(--fg))] hover:bg-muted'
                        }`}
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Which sub-field */}
            {editing ? (
              <p className="text-xs text-globe-grey">
                This is{' '}
                <span className="font-medium text-[hsl(var(--fg))]">
                  {draft.companyEntityLabel || draft.companyKey}
                </span>
                ’s <span className="font-medium">{COMPANY_SUB_LABELS[draft.companySubKind]}</span>.
              </p>
            ) : draft.newCompany ? (
              <p className="text-xs text-globe-grey">
                Marking the company’s <span className="font-medium">Name</span>. Mark its other
                details (address, website, email, phone) afterwards and attach each to this company.
              </p>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">This text is the…</Label>
                <div className="grid grid-cols-3 gap-1">
                  {COMPANY_SUB_KINDS.map((s) => {
                    const Icon = COMPANY_SUB_ICON[s];
                    const disabled = companyTaken.has(s);
                    const active = draft.companySubKind === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        disabled={disabled}
                        onClick={() => set({ companySubKind: s, required: s === 'name' })}
                        aria-pressed={active}
                        className={`flex items-center justify-center gap-1 rounded-md border px-1.5 py-1.5 text-xs transition-colors disabled:opacity-30 ${
                          active
                            ? 'border-selise-blue bg-selise-blue/10 text-selise-blue font-medium'
                            : 'border-border text-globe-grey hover:bg-muted'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        {COMPANY_SUB_LABELS[s]}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Company label — owned by the Name slot, like a person's. */}
            {(draft.newCompany || draft.companySubKind === 'name') && (
              <div className="space-y-1.5">
                <Label htmlFor="draft-company-entity-label" className="text-xs">
                  Company label
                </Label>
                <Input
                  id="draft-company-entity-label"
                  value={draft.companyEntityLabel}
                  onChange={(e) => set({ companyEntityLabel: e.target.value })}
                  className="h-9"
                  placeholder="e.g. Employer"
                />
                <p className="text-[11px] text-globe-grey">
                  How this company is titled in the fill-in form. Its details are entered once and
                  can be pulled straight from your company list.
                </p>
              </div>
            )}

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={draft.required}
                onChange={(e) => set({ required: e.target.checked })}
                className="size-4 rounded border-border accent-selise-blue"
              />
              Required
            </label>
          </>
        )}

        {draftError && (
          <p className="text-xs text-crimson bg-crimson/10 rounded-md p-2">{draftError}</p>
        )}
      </div>

      {/* Actions */}
      <div className="shrink-0 px-4 py-3 border-t border-border flex gap-3 flex-row-reverse">
        <Button onClick={commitDraft} variant="primary" size="sm">
          <Check className="w-3.5 h-3.5 mr-1.5" />
          {editing ? 'Update field' : 'Add field'}
        </Button>
        <Button variant="outline" onClick={cancelDraft} size="sm">
          Cancel
        </Button>
      </div>
    </div>
  );
}
